import React from 'react';
import { Link } from 'react-router-dom';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { getLocaleDateFormatTag } from '@/i18n/localeCatalog';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

function fmtDate(ts, locale) {
  if (!ts) return 'N/A';
  try {
    return new Date(Number(ts)).toLocaleString(getLocaleDateFormatTag(locale));
  } catch {
    return 'N/A';
  }
}

export default function ClassDetailPage({ session, classId }) {
  const { t, locale } = useAppTranslation();
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const isTeacher = session?.role === 'teacher' || session?.role === 'admin' || session?.role === 'super_admin';
  const isStudent = session?.role === 'student';
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);
  const [studentId, setStudentId] = React.useState('');
  const [quizId, setQuizId] = React.useState('');
  const [result, setResult] = React.useState('');

  const load = React.useCallback(async () => {
    if (!token || !classId) return;
    setLoading(true);
    setError('');
    try {
      const x = await api.getClassDetail(classId, token);
      setData(x);
    } catch (e) {
      setError(formatApiErrorMessage(e?.message, t) || t('errors.loadClassDetail'));
    } finally {
      setLoading(false);
    }
  }, [api, classId, token, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  const cls = data?.class;
  const students = Array.isArray(data?.students) ? data.students : [];
  const quizzes = Array.isArray(data?.quizzes) ? data.quizzes : [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-headline text-3xl font-extrabold">{t('class.detailTitle')}</h1>
        <Link
          to="/classes"
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#004ac6] hover:text-[#004ac6]"
        >
          <span className="material-symbols-outlined !text-base">arrow_back</span>
          {t('class.backToList')}
        </Link>
      </div>

      {loading ? <p className="text-sm text-slate-600">{t('class.loading')}</p> : null}
      {error ? <p className="text-sm font-semibold text-rose-700">{error}</p> : null}

      {cls ? (
        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-extrabold text-slate-900">{cls.name}</h2>
          <p className="mt-1 text-sm text-slate-600">{cls.description || t('class.noDesc')}</p>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
            <span>{t('class.teacher')}: {cls.teacher_name || cls.teacher_id}</span>
            <span>{t('class.created')}: {fmtDate(cls.created_at, locale)}</span>
            <span>{t('class.updated')}: {fmtDate(cls.updated_at, locale)}</span>
          </div>
        </section>
      ) : null}

      {isTeacher ? (
        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-bold">{t('class.quickActions')}</h3>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <p className="text-sm font-semibold">{t('class.addStudentLabel')}</p>
              <input
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('class.studentInputPlaceholder')}
              />
              <button
                type="button"
                onClick={async () => {
                  if (!studentId.trim()) return;
                  try {
                    const value = studentId.trim();
                    await api.addStudentToClass(
                      classId,
                      value,
                      token,
                      value.includes('@') ? { student_email: value } : { student_id: value }
                    );
                    setResult(t('class.addStudentSuccess'));
                    setStudentId('');
                    await load();
                  } catch (e) {
                    setResult(formatApiErrorMessage(e?.message, t) || t('class.addStudentFail'));
                  }
                }}
                className="rounded-xl bg-cyan-600 px-4 py-2 text-xs font-bold text-white"
              >
                {t('class.addStudentBtn')}
              </button>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold">{t('class.addQuizLabel')}</p>
              <input
                value={quizId}
                onChange={(e) => setQuizId(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('class.quizInputPlaceholder')}
              />
              <button
                type="button"
                onClick={async () => {
                  if (!quizId.trim()) return;
                  try {
                    const v = quizId.trim();
                    await api.addQuizToClass(
                      classId,
                      v.startsWith('quiz_') ? { quiz_id: v } : { quiz_id: v, share_code: v },
                      token
                    );
                    setResult(t('class.addQuizSuccess'));
                    setQuizId('');
                    await load();
                  } catch (e) {
                    setResult(formatApiErrorMessage(e?.message, t) || t('class.addQuizFail'));
                  }
                }}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white"
              >
                {t('class.addQuizBtn')}
              </button>
            </div>
          </div>
          {result ? <p className="mt-2 text-sm font-semibold text-emerald-700">{result}</p> : null}
        </section>
      ) : null}

      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-bold">{t('class.studentListTitle', { count: students.length })}</h3>
        <div className="mt-3 space-y-2">
          {students.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 p-3">
              <div>
                <p className="font-semibold text-slate-900">{s.name}</p>
                <p className="text-xs text-slate-500">{s.email || t('class.na')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(s.id)}`}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-cyan-500 hover:text-cyan-700"
                >
                  {t('class.studentDetailBtn')}
                </Link>
                {isTeacher ? (
                  <button
                    type="button"
                    onClick={async () => {
                      await api.removeStudentFromClass(classId, s.id, token);
                      await load();
                    }}
                    className="rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700"
                  >
                    {t('class.remove')}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
          {students.length === 0 ? <p className="text-sm text-slate-500">{t('class.noStudents')}</p> : null}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-bold">
          {isStudent
            ? t('class.assignedQuizTitle', { count: quizzes.length })
            : t('class.classQuizTitle', { count: quizzes.length })}
        </h3>
        <div className="mt-3 space-y-2">
          {quizzes.map((q) => (
            <div key={q.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 p-3">
              <div>
                <p className="font-semibold text-slate-900">{q.title}</p>
                {isStudent ? (
                  <p className="text-xs text-slate-500">
                    {q.latest_result_id
                      ? t('class.assignedWithDoneMeta', {
                          assignedAt: fmtDate(q.assigned_at, locale),
                          dueAt: fmtDate(q.due_at, locale),
                          score: q.latest_score,
                          at: fmtDate(q.latest_timestamp, locale),
                        })
                      : t('class.assignedMetaNotDone', {
                          assignedAt: fmtDate(q.assigned_at, locale),
                          dueAt: fmtDate(q.due_at, locale),
                        })}
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">
                    {t('class.teacherQuizMeta', {
                      assignedAt: fmtDate(q.assigned_at, locale),
                      doneStudents: q.students_done || 0,
                      attempts: q.attempts || 0,
                    })}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isStudent ? (
                  q.latest_result_id ? (
                    <Link
                      to={`/history/${encodeURIComponent(String(q.latest_result_id))}`}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-cyan-500 hover:text-cyan-700"
                    >
                      {t('class.viewHistory')}
                    </Link>
                  ) : q.share_code ? (
                    <Link
                      to={`/play/${encodeURIComponent(q.share_code)}`}
                      className="rounded-lg border border-cyan-300 px-3 py-1.5 text-xs font-semibold text-cyan-700"
                    >
                      {t('class.playNow')}
                    </Link>
                  ) : null
                ) : (
                  <Link
                    to={`/classes/${encodeURIComponent(classId)}/quizzes/${encodeURIComponent(q.id)}`}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-cyan-500 hover:text-cyan-700"
                  >
                    {t('class.studentResults')}
                  </Link>
                )}
                {isTeacher ? (
                  <button
                    type="button"
                    onClick={async () => {
                      await api.removeQuizFromClass(classId, q.id, token);
                      await load();
                    }}
                    className="rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700"
                  >
                    {t('class.removeQuiz')}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
          {quizzes.length === 0 ? <p className="text-sm text-slate-500">{t('class.noAssignmentsYet')}</p> : null}
        </div>
      </section>
    </div>
  );
}
